/* Saint Maison — storefront behaviour. No dependencies. */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const format = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const moneyFormatter = new Intl.NumberFormat(`${theme.locale || 'es'}-${theme.country || 'ES'}`, {
    style: 'currency',
    currency: theme.currency || 'EUR',
  });
  const money = (cents) => moneyFormatter.format((Number(cents) || 0) / 100);

  const liveRegion = () => qs('[data-live-region]');
  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => { region.textContent = message; });
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Sheets: native <dialog> with a slide-in panel ---------------- */

  const Sheets = {
    open(dialog) {
      if (!dialog || dialog.open) return;
      dialog.showModal();
      dialog.scrollTop = 0;
      dialog.scrollLeft = 0;
      dialog.dispatchEvent(new CustomEvent('sheet:open'));
      if (reduceMotion.matches) {
        dialog.classList.add('is-open');
        return;
      }
      // Two frames so the closed transform is painted before the open one.
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => dialog.classList.add('is-open')));
    },
    close(dialog) {
      if (!dialog || !dialog.open) return;
      dialog.classList.remove('is-open');
      const finish = () => {
        dialog.close();
        dialog.dispatchEvent(new CustomEvent('sheet:close'));
      };
      if (reduceMotion.matches) return finish();
      window.setTimeout(finish, 260);
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-sheet]');
    if (opener) {
      event.preventDefault();
      Sheets.open(document.getElementById(opener.dataset.openSheet));
      return;
    }
    const closer = event.target.closest('[data-close-sheet]');
    if (closer) {
      event.preventDefault();
      Sheets.close(closer.closest('dialog'));
    }
  });

  document.addEventListener('cancel', (event) => {
    if (event.target.matches('dialog[data-sheet]')) {
      event.preventDefault();
      Sheets.close(event.target);
    }
  }, true);

  /* ---------- Cart ------------------------------------------------------------ */

  const Cart = {
    sections: ['cart-drawer', 'cart-count'],

    async add(variantId, quantity = 1) {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          sections: this.sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'add failed');
      this.render(data.sections);
      const key = (data.items && data.items[0] && data.items[0].key) || data.key;
      this.openDrawer(key);
      return data;
    },

    async change(key, quantity) {
      const response = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: this.sections.join(','), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'change failed');
      this.render(data.sections);
      await this.refreshCartPage();
      return data;
    },

    render(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        const next = qs('[data-cart-inner]', parseHTML(sections['cart-drawer']));
        const current = qs('#CartDrawer [data-cart-inner]');
        if (next && current) current.innerHTML = next.innerHTML;
      }
      if (sections['cart-count']) {
        const next = qs('[data-cart-count]', parseHTML(sections['cart-count']));
        if (next) qsa('[data-cart-count]').forEach((node) => { node.textContent = next.textContent; });
      }
    },

    async refreshCartPage() {
      const page = qs('[data-cart-page]');
      if (!page) return;
      const wrapper = page.closest('.shopify-section');
      if (!wrapper) return;
      const id = wrapper.id.replace('shopify-section-', '');
      const html = await fetch(`${theme.routes.cart}?section_id=${encodeURIComponent(id)}`).then((r) => r.text());
      const next = qs('[data-cart-page]', parseHTML(html));
      if (next) page.replaceWith(next);
    },

    openDrawer(newKey) {
      const dialog = document.getElementById('CartDrawer');
      if (!dialog) {
        window.location.href = theme.routes.cart;
        return;
      }
      if (newKey) {
        const line = qs(`[data-line-key="${CSS.escape(newKey)}"]`, dialog);
        if (line) line.classList.add('is-new');
      }
      Sheets.open(dialog);
    },
  };

  window.SaintCart = Cart;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-open-cart]');
    if (!link || window.location.pathname === theme.routes.cart) return;
    event.preventDefault();
    Cart.openDrawer();
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-qty-change]');
    if (!button) return;
    const line = button.closest('[data-line-key]');
    if (!line) return;
    line.classList.add('is-updating');
    try {
      await Cart.change(line.dataset.lineKey, Number(button.dataset.qtyChange));
    } catch (error) {
      line.classList.remove('is-updating');
      announce(theme.strings.addError);
    }
  });

  const setLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', String(loading));
    if (loading) button.setAttribute('aria-label', theme.strings.adding);
    else button.removeAttribute('aria-label');
  };

  const alertPills = (pills) => {
    if (!pills) return;
    pills.classList.remove('is-alerting');
    void pills.offsetWidth;
    pills.classList.add('is-alerting');
    const first = qs('input:not([disabled])', pills);
    if (first) first.focus({ preventScroll: true });
    announce(theme.strings.chooseSizeAlert);
  };

  /* ---------- Header ---------------------------------------------------------- */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.overlay !== 'true') return;
      this.update = this.update.bind(this);
      window.addEventListener('scroll', this.update, { passive: true });
      this.update();
    }
    update() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.classList.toggle('is-solid', window.scrollY > 8);
      });
    }
  }
  customElements.define('site-header', SiteHeader);

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announcement__item', this);
      if (this.items.length < 2 || reduceMotion.matches) return;
      this.index = 0;
      this.paused = false;
      this.addEventListener('mouseenter', () => { this.paused = true; });
      this.addEventListener('mouseleave', () => { this.paused = false; });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });
      this.timer = window.setInterval(() => this.next(), Number(this.dataset.interval) || 5000);
    }
    disconnectedCallback() { window.clearInterval(this.timer); }
    next() {
      if (this.paused || document.hidden) return;
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Predictive search ------------------------------------------------ */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('[data-search-input]', this);
      this.results = qs('[data-predictive-results]', this);
      const dialog = this.closest('dialog');
      if (dialog) dialog.addEventListener('sheet:open', () => window.setTimeout(() => this.input.focus(), 60));
      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(this.input.value.trim()), 180);
      });
    }
    async search(term) {
      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      const url = `${theme.routes.predictiveSearch}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6`;
      try {
        const data = await fetch(url).then((r) => r.json());
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) {
          this.results.innerHTML = `<p class="predictive__empty text"></p>`;
          qs('.predictive__empty', this.results).textContent = theme.strings.noResults;
          return;
        }
        this.results.innerHTML = products.map((p) => `
          <a class="predictive__item" href="${p.url}">
            <span class="media">${p.image ? `<img src="${p.image}${p.image.includes('?') ? '&' : '?'}width=160" alt="" loading="lazy" width="56" height="84">` : ''}</span>
            <span><span class="predictive__title"></span><span class="predictive__price label muted">${money(Math.round(parseFloat(p.price) * 100))}</span></span>
          </a>`).join('');
        qsa('.predictive__title', this.results).forEach((node, i) => { node.textContent = products[i].title; });
      } catch (error) {
        this.results.innerHTML = '';
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Delivery estimate ------------------------------------------------ */

  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return result;
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min);
      const max = Number(this.dataset.max);
      if (!min || !max) return;
      const today = new Date();
      const formatter = new Intl.DateTimeFormat(`${theme.locale || 'es'}-${theme.country || 'ES'}`, { day: 'numeric', month: 'short' });
      const start = formatter.format(addBusinessDays(today, min)).replace('.', '');
      const end = formatter.format(addBusinessDays(today, max)).replace('.', '');
      this.textContent = format(theme.strings.deliveryBetween, { start, end });
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Product gallery -------------------------------------------------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-gallery-track]', this);
      this.slides = qsa('.gallery__slide', this);
      this.bars = qsa('.gallery__progress span', this);
      if (this.bars.length && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = this.visible ? this.visible.indexOf(entry.target) : Number(entry.target.dataset.index);
            if (index >= 0) this.setActive(index);
          });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach((slide) => observer.observe(slide));
      }
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-zoom-index]');
        if (button) this.zoom(Number(button.dataset.zoomIndex));
      });
    }
    setActive(index) {
      this.bars.forEach((bar, i) => bar.classList.toggle('is-active', i === index));
    }
    // Products without per-colour photo groups: bring the variant's photo into view.
    showMedia(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      if (desktop.matches) slide.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      else this.track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    // Groups follow the store's media order: each colour's photos start at that colour's
    // variant image and run until the next colour's image. Photos before the first one are shared.
    colorGroups(startToColor) {
      const groups = new Map();
      let current = null;
      this.slides.forEach((slide) => {
        const id = slide.dataset.mediaId;
        if (startToColor.has(id)) current = startToColor.get(id);
        groups.set(id, current);
      });
      return groups;
    }

    filterByColor(groups, color) {
      // A colour without its own photos in the gallery shows every photo rather than none.
      const hasOwn = [...groups.values()].includes(color);
      const matches = (id) => {
        if (!hasOwn) return true;
        const group = groups.get(id);
        return group == null || group === color;
      };
      this.visible = this.slides.filter((slide) => matches(slide.dataset.mediaId));
      this.slides.forEach((slide) => {
        slide.hidden = !this.visible.includes(slide);
        slide.classList.remove('is-lead');
      });
      if (this.visible[0]) this.visible[0].classList.add('is-lead');

      const progress = qs('.gallery__progress', this);
      if (progress) {
        progress.innerHTML = this.visible.map((_, i) => `<span${i === 0 ? ' class="is-active"' : ''}></span>`).join('');
        this.bars = qsa('span', progress);
        progress.hidden = this.visible.length < 2;
      }
      this.track.scrollTo({ left: 0 });

      const section = this.closest('[data-section]');
      const dialog = section && document.getElementById(`Zoom-${section.dataset.section}`);
      if (dialog) qsa('[data-zoom-item]', dialog).forEach((img) => { img.hidden = !matches(img.dataset.mediaId); });
    }
    zoom(index) {
      const section = this.closest('[data-section]');
      const dialog = section && document.getElementById(`Zoom-${section.dataset.section}`);
      if (!dialog) return;
      qsa('[data-zoom-item]', dialog).forEach((img) => {
        if (!img.src && img.dataset.src) img.src = img.dataset.src;
      });
      Sheets.open(dialog);
      const target = qs(`[data-zoom-item="${index}"]`, dialog);
      if (target) window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Variant picker and product form ---------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(qs('[data-variants]', this).textContent);
      this.form = document.getElementById(this.dataset.form);
      this.info = this.closest('product-info');
      this.fieldsets = qsa('fieldset[data-option-index]', this);
      this.gallery = qs('product-gallery');
      this.setupColorGroups();
      this.addEventListener('change', () => this.update());
      this.refreshAvailability();
    }

    setupColorGroups() {
      this.colorIndex = this.fieldsets.findIndex((f) => f.hasAttribute('data-color-option'));
      if (this.colorIndex < 0 || !this.gallery || !this.gallery.colorGroups) return;
      const startToColor = new Map();
      const seen = new Set();
      this.variants.forEach((v) => {
        const color = v.options[this.colorIndex];
        if (!v.media_id || seen.has(color) || startToColor.has(String(v.media_id))) return;
        seen.add(color);
        startToColor.set(String(v.media_id), color);
      });
      if (startToColor.size < 2) return;
      this.groups = this.gallery.colorGroups(startToColor);
      this.applyColor();
    }

    applyColor() {
      if (!this.groups) return false;
      const checked = qs('input:checked', this.fieldsets[this.colorIndex]);
      if (!checked) return false;
      if (checked.value !== this.appliedColor) {
        this.appliedColor = checked.value;
        this.gallery.filterByColor(this.groups, checked.value);
      }
      return true;
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    refreshAvailability() {
      const selected = this.selectedOptions();
      this.fieldsets.forEach((fieldset, index) => {
        qsa('input', fieldset).forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((value, i) => candidate[i] === null || i === index || value === candidate[i]) && v.options[index] === input.value);
          const available = Boolean(match && match.available);
          if (fieldset.hasAttribute('data-size-option')) input.disabled = !available && !input.checked;
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    update() {
      const selected = this.selectedOptions();
      this.refreshAvailability();
      const colorValue = qs('[data-option-value]', this);
      const colorFieldset = colorValue && qs('fieldset', colorValue.closest('.option'));
      if (colorValue && colorFieldset) {
        const checked = qs('input:checked', colorFieldset);
        if (checked) colorValue.textContent = `— ${checked.value}`;
      }

      const complete = selected.every((value) => value !== null);
      const variant = complete ? this.variants.find((v) => v.options.every((value, i) => value === selected[i])) : null;
      const input = this.form && qs('[data-variant-input]', this.form);
      const button = this.form && qs('[data-add-button]', this.form);

      if (input) input.value = variant ? variant.id : '';
      if (button) {
        if (!variant) {
          button.disabled = false;
          button.textContent = complete ? theme.strings.unavailable : theme.strings.chooseSize;
          if (complete) button.disabled = true;
        } else if (!variant.available) {
          button.disabled = true;
          button.textContent = theme.strings.soldOut;
        } else {
          button.disabled = false;
          button.textContent = theme.strings.addToCart;
        }
      }

      const reference = variant || this.variants.find((v) => v.options.every((value, i) => selected[i] === null || value === selected[i]));
      if (reference) {
        this.renderPrice(reference);
        const filtered = this.applyColor();
        if (!filtered && reference.media_id && this.gallery && this.gallery.showMedia) this.gallery.showMedia(reference.media_id);
      }

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, complete } }));
    }

    renderPrice(variant) {
      const scope = this.info || document;
      const current = qs('[data-price-current]', scope);
      if (current) current.textContent = money(variant.price);
      const price = qs('[data-price]', scope);
      if (!price) return;
      let compare = qs('[data-price-compare]', price);
      let save = qs('[data-price-save]', price);
      const onSale = variant.compare_at_price > variant.price;
      if (onSale) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          compare.dataset.priceCompare = '';
          price.appendChild(compare);
        }
        compare.textContent = money(variant.compare_at_price);
        if (save) save.textContent = format(theme.strings.you_save, { amount: money(variant.compare_at_price - variant.price) });
      }
      if (compare) compare.hidden = !onSale;
      if (save) save.hidden = !onSale;
      const sticky = qs('[data-sticky-price]');
      if (sticky) sticky.textContent = money(variant.price);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form.product-form');
    if (!form) return;
    event.preventDefault();
    const input = qs('[data-variant-input]', form);
    const button = qs('[data-add-button]', form);
    const error = qs('[data-add-error]', form);
    if (error) error.hidden = true;

    if (!input || !input.value) {
      const picker = document.querySelector(`variant-picker[data-form="${form.getAttribute('id')}"]`);
      const sizeFieldset = picker && qs('[data-size-option]', picker);
      alertPills(sizeFieldset && qs('[data-pills]', sizeFieldset));
      return;
    }

    setLoading(button, true);
    try {
      await Cart.add(input.value, 1);
    } catch (err) {
      if (error) {
        error.textContent = theme.strings.addError;
        error.hidden = false;
      }
    } finally {
      setLoading(button, false);
    }
  });

  /* ---------- Sticky buy strip --------------------------------------------------- */

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      const target = this.form && qs('[data-add-button]', this.form);
      if (!target || !('IntersectionObserver' in window)) return;
      this.button = qs('[data-sticky-add]', this);
      // Shown whenever the real button is off screen: below the fold on arrival, or scrolled past.
      this.observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        this.button.tabIndex = show ? 0 : -1;
      });
      this.observer.observe(target);
      this.button.addEventListener('click', () => this.buy());
      document.addEventListener('variant:change', (event) => {
        const { variant } = event.detail;
        this.button.textContent = variant && variant.available ? theme.strings.addShort : theme.strings.chooseSizeShort;
      });
    }
    buy() {
      const input = qs('[data-variant-input]', this.form);
      if (input && input.value) {
        this.form.requestSubmit();
        return;
      }
      const picker = document.querySelector(`variant-picker[data-form="${this.form.getAttribute('id')}"]`);
      const fieldset = picker && qs('[data-size-option]', picker);
      if (fieldset) {
        fieldset.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        window.setTimeout(() => alertPills(qs('[data-pills]', fieldset)), reduceMotion.matches ? 0 : 380);
      }
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* ---------- Quick add on cards --------------------------------------------------- */

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-quick-add-toggle]');
    if (toggle) {
      const card = toggle.closest('[data-product-card]');
      qsa('.card.is-quick-open').forEach((open) => {
        if (open !== card) open.classList.remove('is-quick-open');
      });
      const isOpen = card.classList.toggle('is-quick-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        const first = qs('.quick-add__size:not([disabled])', card);
        if (first) first.focus({ preventScroll: true });
      }
      return;
    }

    const size = event.target.closest('.quick-add__size');
    if (size) {
      size.classList.add('is-loading');
      try {
        await Cart.add(size.dataset.variantId, 1);
        const card = size.closest('[data-product-card]');
        if (card) card.classList.remove('is-quick-open');
      } catch (error) {
        announce(theme.strings.addError);
      } finally {
        size.classList.remove('is-loading');
      }
      return;
    }

    if (!event.target.closest('[data-product-card]')) {
      qsa('.card.is-quick-open').forEach((open) => open.classList.remove('is-quick-open'));
    }
  });

  /* ---------- Mini add (editorial look, cart upsell) ---------------------------------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-mini-add-button]');
    if (!button) return;
    const scope = button.closest('[data-mini-add]');
    const checked = qs('input[type="radio"]:checked', scope) || qs('[data-default-variant]', scope);
    if (!checked) {
      alertPills(qs('.pills', scope));
      return;
    }
    button.classList.add('is-loading');
    button.disabled = true;
    try {
      await Cart.add(checked.value, 1);
    } catch (error) {
      announce(theme.strings.addError);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  });

  /* ---------- Motion tiles: nothing loads until near the viewport ------------------- */

  class MotionTile extends HTMLElement {
    connectedCallback() {
      this.video = qs('video', this);
      this.button = qs('.motion-tile__toggle', this);
      if (!this.video) return;
      const saveData = navigator.connection && navigator.connection.saveData;
      this.autoplay = !reduceMotion.matches && !saveData;
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && this.autoplay && !this.userPaused) this.play();
        else if (!entry.isIntersecting) this.stop();
      }, { threshold: 0.5 });
      observer.observe(this);
      this.button.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          this.stop();
        }
      });
    }
    play() {
      if (!this.video.src) this.video.src = this.video.dataset.src;
      this.video.play().then(() => this.button.setAttribute('aria-pressed', 'true')).catch(() => {});
    }
    stop() {
      if (!this.video.paused) this.video.pause();
      this.button.setAttribute('aria-pressed', 'false');
    }
  }
  customElements.define('motion-tile', MotionTile);

  /* ---------- Reviews rail ------------------------------------------------------------ */

  class ReviewsRow extends HTMLElement {
    connectedCallback() {
      const row = qs('[data-reviews-row]', this);
      if (!row) return;
      const step = (dir) => row.scrollBy({ left: dir * row.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      const prev = qs('[data-reviews-prev]', this);
      const next = qs('[data-reviews-next]', this);
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
    }
  }
  customElements.define('reviews-row', ReviewsRow);

  /* ---------- Recommendations -------------------------------------------------------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const html = await fetch(this.dataset.url).then((r) => r.text());
          const next = qs('product-recommendations', parseHTML(html));
          if (next && next.innerHTML.trim()) this.innerHTML = next.innerHTML;
        } catch (error) { /* recommendations are optional */ }
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 600px 0px' });
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection: sort, load more -------------------------------------------- */

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-select]');
    if (select) select.form.submit();
  });

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-load-more-link]');
    if (!link) return;
    event.preventDefault();
    link.classList.add('is-loading');
    try {
      const doc = parseHTML(await fetch(link.href).then((r) => r.text()));
      const grid = qs('[data-product-grid]');
      const incoming = qs('[data-product-grid]', doc);
      if (grid && incoming) grid.append(...Array.from(incoming.children));
      const nav = qs('[data-load-more]');
      const nextNav = qs('[data-load-more]', doc);
      if (nav && nextNav) nav.replaceWith(nextNav);
    } catch (error) {
      window.location.href = link.href;
    }
  });

  /* ---------- Footer: open columns on desktop, folded on phones ------------------------ */

  const syncFooter = () => {
    qsa('.footer-col').forEach((details) => { details.open = desktop.matches; });
  };
  syncFooter();
  desktop.addEventListener('change', syncFooter);
})();
